import React, { useMemo } from 'react';

const shortDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return String(iso || '').slice(5, 10);
  }
};

export default function HistoryChart({ history = [], type = 'overview' }) {
  // ----- shared accessors -----
  const consumedFor = (d) => Number(d?.calories_consumed || 0) + Number(d?.wearable_calories_consumed || 0);
  const burnedFor = (d) => Number(d?.calories_burned || 0) + Number(d?.wearable_calories_burned || 0);
  const manualFor = (d) => (typeof d?.weight_manual === 'number' ? d.weight_manual : (Number.isFinite(Number(d?.weight_manual)) ? Number(d.weight_manual) : null));
  const deviceFor = (d) => (typeof d?.weight_device === 'number' ? d.weight_device : (Number.isFinite(Number(d?.weight_device)) ? Number(d.weight_device) : null));

  // Hooks MUST be unconditional.
  const maxCalories = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return 100;
    if (type === 'weight') return 100;

    let maxV = 100;
    for (const d of history) {
      const c = consumedFor(d);
      const b = burnedFor(d);
      if (type === 'food') maxV = Math.max(maxV, c);
      else if (type === 'exercise') maxV = Math.max(maxV, b);
      else maxV = Math.max(maxV, c, b);
    }
    return maxV;
  }, [history, type]);

  const weightPoints = useMemo(() => {
    if (type !== 'weight') return [];
    if (!Array.isArray(history) || history.length === 0) return [];

    // Expect history to already be date-sorted; but be safe.
    const pts = history
      .map((d) => ({
        date: d.date,
        manual: manualFor(d),
        device: deviceFor(d),
      }))
      .filter((p) => !!p.date && (typeof p.manual === 'number' || typeof p.device === 'number'))
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    return pts;
  }, [history, type]);

  // ===================== WEIGHT LINE CHART =====================
  if (type === 'weight') {
    if (!weightPoints.length) {
      return (
        <div style={{ padding: 8, textAlign: 'center', color: 'var(--text-muted)' }}>
          No weight points for this range.
        </div>
      );
    }

    const width = Math.max(520, weightPoints.length * 42);
    const height = 260;
    const pad = { l: 44, r: 18, t: 18, b: 34 };

    const allVals = [];
    for (const p of weightPoints) {
      if (typeof p.manual === 'number') allVals.push(p.manual);
      if (typeof p.device === 'number') allVals.push(p.device);
    }

    const minW = Math.min(...allVals);
    const maxW = Math.max(...allVals);
    const span = Math.max(1, maxW - minW);

    const x = (i) => {
      const usable = width - pad.l - pad.r;
      if (weightPoints.length <= 1) return pad.l + usable / 2;
      return pad.l + (usable * i) / (weightPoints.length - 1);
    };

    const y = (w) => {
      const usable = height - pad.t - pad.b;
      // a bit of breathing room
      const extra = span * 0.08;
      const lo = minW - extra;
      const hi = maxW + extra;
      return pad.t + ((hi - w) / (hi - lo || 1)) * usable;
    };

    const buildPath = (key) => {
      let d = '';
      let started = false;
      for (let i = 0; i < weightPoints.length; i++) {
        const v = weightPoints[i][key];
        if (typeof v !== 'number') {
          started = false;
          continue;
        }
        const px = x(i);
        const py = y(v);
        if (!started) {
          d += `M ${px} ${py}`;
          started = true;
        } else {
          d += ` L ${px} ${py}`;
        }
      }
      return d;
    };

    const manualPath = buildPath('manual');
    const devicePath = buildPath('device');

    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: width }}>
          <svg width={width} height={height} style={{ display: 'block' }}>
            {/* axes */}
            <line x1={pad.l} y1={height - pad.b} x2={width - pad.r} y2={height - pad.b} stroke="currentColor" opacity="0.15" />
            <line x1={pad.l} y1={pad.t} x2={pad.l} y2={height - pad.b} stroke="currentColor" opacity="0.15" />

            {/* y labels (3) */}
            {[0, 0.5, 1].map((t) => {
              const w = minW + span * (1 - t);
              const yy = y(w);
              return (
                <g key={t}>
                  <line x1={pad.l} y1={yy} x2={width - pad.r} y2={yy} stroke="currentColor" opacity="0.06" />
                  <text x={pad.l - 8} y={yy + 4} fontSize="11" textAnchor="end" fill="currentColor" opacity="0.65">
                    {w.toFixed(0)}
                  </text>
                </g>
              );
            })}

            {/* lines */}
            {manualPath && (
              <path d={manualPath} fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.85" />
            )}
            {devicePath && (
              <path
                d={devicePath}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                opacity="0.85"
                strokeDasharray="6 6"
              />
            )}

            {/* points */}
            {weightPoints.map((p, i) => (
              <g key={p.date + i}>
                {typeof p.manual === 'number' && (
                  <circle cx={x(i)} cy={y(p.manual)} r="3.5" fill="currentColor" opacity="0.9" />
                )}
                {typeof p.device === 'number' && (
                  <circle cx={x(i)} cy={y(p.device)} r="3.5" fill="currentColor" opacity="0.9" />
                )}
              </g>
            ))}

            {/* x labels */}
            {weightPoints.map((p, i) => {
              const every = Math.max(1, Math.ceil(weightPoints.length / 8));
              if (i % every !== 0 && i !== weightPoints.length - 1) return null;
              return (
                <text
                  key={'x' + p.date + i}
                  x={x(i)}
                  y={height - 8}
                  fontSize="11"
                  fill="currentColor"
                  opacity="0.65"
                  textAnchor="middle"
                >
                  {shortDate(p.date)}
                </text>
              );
            })}
          </svg>

          <div className="body-small" style={{ marginTop: 8, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>— Manual</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 18, height: 2, background: 'currentColor', opacity: 0.8, display: 'inline-block', borderTop: '2px dashed currentColor' }} />
              Device (Withings / wearables)
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ===================== CALORIES BAR CHART =====================
  return (
    <div>
      <h3 className="heading-3">Progress</h3>
      <div className="history-chart" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', overflowX: 'auto', paddingTop: 10 }}>
        {history.map((day) => {
          const c = consumedFor(day);
          const b = burnedFor(day);

          const showConsumed = type !== 'exercise';
          const showBurned = type !== 'food';

          const consumedHeight = showConsumed ? (c / maxCalories) * 200 : 0;
          const burnedHeight = showBurned ? (b / maxCalories) * 200 : 0;

          return (
            <div key={day.date} style={{ width: 44, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 4, height: 220 }}>
                {showConsumed && (
                  <div
                    title={`Consumed: ${Math.round(c)} cal`}
                    style={{
                      width: 14,
                      height: Math.max(2, consumedHeight),
                      background: 'currentColor',
                      opacity: 0.35,
                      borderRadius: 6,
                    }}
                  />
                )}
                {showBurned && (
                  <div
                    title={`Burned: ${Math.round(b)} cal`}
                    style={{
                      width: 14,
                      height: Math.max(2, burnedHeight),
                      background: 'currentColor',
                      opacity: 0.75,
                      borderRadius: 6,
                    }}
                  />
                )}
              </div>
              <div className="body-small" style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                {shortDate(day.date)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="body-small" style={{ marginTop: 10, color: 'var(--text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {type !== 'exercise' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: 'currentColor', opacity: 0.35, display: 'inline-block' }} />
            Consumed
          </span>
        )}
        {type !== 'food' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: 'currentColor', opacity: 0.75, display: 'inline-block' }} />
            Burned
          </span>
        )}
      </div>
    </div>
  );
}
